import React, { useState, useEffect, useMemo } from 'react';
import { apiService } from '../../services/apiService';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line,
} from 'recharts';
import {
  Zap, Database, BarChart3, Activity, CreditCard, Cpu, TrendingUp, Check, Minus,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

interface UsageData {
  totalRequests: number;
  totalCreditsUsed: number;
  totalTokensUsed: number;
  apiKeysCount: number;
  activeKeysCount: number;
  total: number;
  dailyUsage: Record<string, { requests: number; credits: number; tokens: number }>;
  recentUsage: Array<{
    id: string;
    timestamp: Date;
    modelUsed: string;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    creditsCost: number;
    taskType: string;
    endpoint: string;
    apiKeyId: string;
    webSearchUsed: boolean;
    reasoningUsed: boolean;
    hasImage: boolean;
    hasDocument: boolean;
    processingTimeMs: number;
    ipAddress: string;
    taskDeterminationUsed: boolean;
    complexity: number;
    functionCallingUsed: boolean;
  }>;
}

interface TDUsageData {
  plan: { displayName: string; freeTaskDetQuota: number };
  freeQuotaRemaining: number;
  currentPeriodCount: number;
  totalLifetime: number;
  nextResetDate: string | null;
  isAdmin: boolean;
}

const PAGE_SIZE = 25;

// ─── Stat card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ReactNode;
  iconBg: string;
  title: string;
  value: React.ReactNode;
  subtitle?: string;
  progress?: { used: number; total: number };
}

function StatCard({ icon, iconBg, title, value, subtitle, progress }: StatCardProps) {
  const hasProgress = progress && progress.total > 0;
  const pct = hasProgress ? Math.min(100, Math.round((progress!.used / progress!.total) * 100)) : 0;
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5 leading-tight">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {hasProgress && (
        <div className="space-y-1">
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${pct >= 90 ? 'bg-red-400' : pct >= 70 ? 'bg-amber-400' : 'bg-blue-400'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-xs text-gray-400">{pct}% used</p>
        </div>
      )}
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3 animate-pulse">
      <div className="w-10 h-10 rounded-lg bg-gray-100" />
      <div className="space-y-2">
        <div className="h-3 w-20 bg-gray-100 rounded" />
        <div className="h-7 w-28 bg-gray-100 rounded" />
        <div className="h-3 w-24 bg-gray-100 rounded" />
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{children}</h2>
      <div className="mt-1 border-t border-gray-100" />
    </div>
  );
}

// ─── Usage ────────────────────────────────────────────────────────────────────

type Timeframe = '7d' | '30d' | 'all';

function getDateParams(timeframe: Timeframe): { dateFrom?: string; dateTo?: string } {
  if (timeframe === 'all') return {};
  const days = timeframe === '7d' ? 7 : 30;
  const from = new Date();
  from.setDate(from.getDate() - days);
  from.setHours(0, 0, 0, 0);
  return { dateFrom: from.toISOString(), dateTo: new Date().toISOString() };
}

export default function Usage() {
  const [data, setData] = useState<UsageData | null>(null);
  const [tdUsage, setTdUsage] = useState<TDUsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeframe, setTimeframe] = useState<Timeframe>('7d');
  const [modelFilter, setModelFilter] = useState('');
  const [page, setPage] = useState(1);

  // Per-session cache: timeframe → fetched data (cleared on page reload)
  const cache = React.useRef<Partial<Record<Timeframe, UsageData>>>({});

  // Initial load: fetch TD data once + 7d usage data
  useEffect(() => {
    const loadAll = async () => {
      try {
        const subscriptionsEnabled = process.env.REACT_APP_ENABLE_SUBSCRIPTIONS !== 'false';
        const [usageData, tdData] = await Promise.all([
          apiService.getUsageAnalytics(getDateParams('7d')),
          subscriptionsEnabled ? apiService.getTaskDetUsage().catch(() => null) : Promise.resolve(null),
        ]);
        cache.current['7d'] = usageData;
        setData(usageData);
        setTdUsage(tdData);
      } catch (err: any) {
        setError(err.message || 'Failed to load usage data');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  // Switch timeframe: use cache if available, otherwise fetch and cache
  useEffect(() => {
    if (loading) return; // skip during initial load
    if (cache.current[timeframe]) {
      setData(cache.current[timeframe]!);
      setPage(1);
      return;
    }
    const refetch = async () => {
      setTableLoading(true);
      setPage(1);
      try {
        const usageData = await apiService.getUsageAnalytics(getDateParams(timeframe));
        cache.current[timeframe] = usageData;
        setData(usageData);
      } catch (err: any) {
        setError(err.message || 'Failed to load usage data');
      } finally {
        setTableLoading(false);
      }
    };
    refetch();
  }, [timeframe]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset page when model filter changes
  useEffect(() => { setPage(1); }, [modelFilter]);

  // ── Derived values (all before any early return) ───────────────────────────

  const chartDays = timeframe === '7d' ? 7 : 30;

  // Compute period stats from recentUsage so they always match the table count
  const periodStats = useMemo(() => {
    return (data?.recentUsage ?? []).reduce(
      (acc, r) => ({
        requests: acc.requests + 1,
        credits: acc.credits + (r.creditsCost || 0),
        tokens: acc.tokens + (r.totalTokens || 0),
      }),
      { requests: 0, credits: 0, tokens: 0 }
    );
  }, [data]);

  const avgCostPerRequest = periodStats.requests > 0
    ? (periodStats.credits / periodStats.requests).toFixed(6)
    : '0.000000';

  const chartData = useMemo(() => {
    if (!data?.dailyUsage) return [];
    if (timeframe === 'all') {
      return Object.entries(data.dailyUsage)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateStr, b]) => {
          const d = new Date(dateStr);
          return {
            date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            requests: b.requests,
            credits: b.credits,
          };
        });
    }
    return Array.from({ length: chartDays }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (chartDays - 1 - i));
      const key = d.toISOString().split('T')[0];
      return {
        date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        requests: data.dailyUsage[key]?.requests || 0,
        credits: data.dailyUsage[key]?.credits || 0,
      };
    });
  }, [data, timeframe, chartDays]);

  // All records already date-filtered by server — just apply model filter client-side
  const filteredUsage = useMemo(() => {
    const all = data?.recentUsage ?? [];
    if (!modelFilter) return all;
    return all.filter(u => u.modelUsed === modelFilter);
  }, [data, modelFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsage.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedUsage = filteredUsage.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Unique models from all fetched records (for dropdown)
  const allModels = useMemo(() => {
    const models = new Set((data?.recentUsage ?? []).map(u => u.modelUsed).filter(Boolean));
    return Array.from(models).sort();
  }, [data]);

  // Top models from all fetched records (already date-filtered by server)
  const topModels = useMemo(() => {
    const counts = new Map<string, number>();
    (data?.recentUsage ?? []).forEach(u => {
      if (u.modelUsed) counts.set(u.modelUsed, (counts.get(u.modelUsed) ?? 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [data]);

  const topModelMax = topModels[0]?.[1] ?? 1;

  // TD helpers
  const freeQuotaTotal = tdUsage?.plan?.freeTaskDetQuota ?? 0;
  const freeQuotaUsed = freeQuotaTotal - (tdUsage?.freeQuotaRemaining ?? 0);

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const planLabel = tdUsage?.isAdmin ? 'Unlimited' : (tdUsage?.plan?.displayName ?? '—');

  const timeframeLabel: Record<Timeframe, string> = { '7d': '7 days', '30d': '30 days', 'all': 'All' };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usage Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">Track your API usage and spending patterns</p>
        </div>
        <div className="bg-gray-100 rounded-lg p-1 flex gap-1">
          {(['7d', '30d', 'all'] as Timeframe[]).map(tf => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`px-4 py-1.5 text-sm rounded-md transition-all ${
                timeframe === tf
                  ? 'bg-white shadow-sm font-medium text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {timeframeLabel[tf]}
            </button>
          ))}
        </div>
      </div>

      {/* Task Determinations */}
      <div className="space-y-3">
        <SectionLabel>Task Determinations</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {loading ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />) : (
            <>
              <StatCard
                icon={<Zap size={20} className="text-purple-600" />}
                iconBg="bg-purple-50"
                title="Current Plan"
                value={
                  tdUsage?.isAdmin
                    ? <span className="flex items-center gap-1.5">Unlimited <span className="text-xs font-medium bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Admin</span></span>
                    : planLabel
                }
                subtitle={tdUsage?.nextResetDate ? `Resets ${formatDate(tdUsage.nextResetDate)}` : 'Manage subscription'}
              />
              <StatCard
                icon={<Database size={20} className="text-blue-600" />}
                iconBg="bg-blue-50"
                title="Free Quota"
                value={tdUsage ? `${(tdUsage.freeQuotaRemaining ?? 0).toLocaleString()} left` : '—'}
                subtitle={freeQuotaTotal > 0 ? `of ${freeQuotaTotal.toLocaleString()} this period` : 'This billing period'}
                progress={freeQuotaTotal > 0 ? { used: freeQuotaUsed, total: freeQuotaTotal } : undefined}
              />
              <StatCard
                icon={<BarChart3 size={20} className="text-amber-600" />}
                iconBg="bg-amber-50"
                title="Period Task Dets."
                value={tdUsage ? (tdUsage.currentPeriodCount ?? 0).toLocaleString() : '—'}
                subtitle="This billing period"
              />
              <StatCard
                icon={<Activity size={20} className="text-indigo-600" />}
                iconBg="bg-indigo-50"
                title="Lifetime Task Dets."
                value={tdUsage ? (tdUsage.totalLifetime ?? 0).toLocaleString() : '—'}
                subtitle="All-time total"
              />
            </>
          )}
        </div>
      </div>

      {/* API Usage */}
      <div className="space-y-3">
        <SectionLabel>API Usage</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {loading ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />) : (
            <>
              <StatCard
                icon={<TrendingUp size={20} className="text-green-600" />}
                iconBg="bg-green-50"
                title="API Requests"
                value={tableLoading ? '…' : periodStats.requests.toLocaleString()}
                subtitle="In selected period"
              />
              <StatCard
                icon={<CreditCard size={20} className="text-rose-600" />}
                iconBg="bg-rose-50"
                title="Credits Used"
                value={tableLoading ? '…' : `$${periodStats.credits.toFixed(4)}`}
                subtitle="In selected period"
              />
              <StatCard
                icon={<Cpu size={20} className="text-teal-600" />}
                iconBg="bg-teal-50"
                title="Tokens Used"
                value={tableLoading ? '…' : periodStats.tokens.toLocaleString()}
                subtitle="In selected period"
              />
              <StatCard
                icon={<BarChart3 size={20} className="text-gray-500" />}
                iconBg="bg-gray-50"
                title="Avg Cost / Request"
                value={tableLoading ? '…' : `$${avgCostPerRequest}`}
                subtitle="Per API call"
              />
            </>
          )}
        </div>
      </div>

      {/* Charts + Most Used Models */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Daily Requests</h3>
            {tableLoading ? (
              <div className="h-[220px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip />
                  <Bar dataKey="requests" fill="#6366f1" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Daily Credits Used</h3>
            {tableLoading ? (
              <div className="h-[220px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip formatter={(value) => [`$${Number(value).toFixed(4)}`, 'Credits']} />
                  <Line type="monotone" dataKey="credits" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Most Used Models */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Most Used Models</h3>
            {tableLoading ? (
              <div className="h-[220px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
              </div>
            ) : topModels.length === 0 ? (
              <div className="flex items-center justify-center h-[220px]">
                <p className="text-sm text-gray-400">No data in this period</p>
              </div>
            ) : (
              <div className="space-y-3 mt-1">
                {topModels.map(([model, count]) => (
                  <div key={model}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600 truncate max-w-[75%]" title={model}>{model}</span>
                      <span className="text-xs font-medium text-gray-900 ml-2">{count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-indigo-400 transition-all"
                        style={{ width: `${Math.round((count / topModelMax) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* API Calls Table */}
      {!loading && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700">API Calls</h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {tableLoading ? 'Loading…' : `${filteredUsage.length.toLocaleString()} call${filteredUsage.length !== 1 ? 's' : ''} in selected period`}
              </p>
            </div>
            {allModels.length > 0 && (
              <select
                value={modelFilter}
                onChange={e => setModelFilter(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All models</option>
                {allModels.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            )}
          </div>

          <div className="overflow-x-auto min-h-[320px]">
            {tableLoading ? (
              <div className="flex items-center justify-center h-[320px]">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
              </div>
            ) : filteredUsage.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[320px]">
                <BarChart3 className="h-10 w-10 text-gray-300 mb-3" />
                <p className="text-sm text-gray-400">No API calls in this period</p>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>
                    {['Date', 'Model', 'Input Tokens', 'Output Tokens', 'Cost', 'Complexity', 'Task Det.', 'Features'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {paginatedUsage.map((usage, index) => (
                    <tr key={usage.id || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                        {new Date(usage.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                          {usage.modelUsed}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                        {usage.inputTokens.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                        {usage.outputTokens.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs">
                        {usage.creditsCost === 0
                          ? <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700 rounded-full">Free</span>
                          : <span className="text-gray-700">${usage.creditsCost.toFixed(6)}</span>
                        }
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center text-xs text-gray-600">
                        {usage.complexity > 0 ? usage.complexity : <Minus size={14} className="text-gray-300 mx-auto" />}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {usage.taskDeterminationUsed
                          ? <Check size={14} className="text-indigo-500 mx-auto" />
                          : <Minus size={14} className="text-gray-300 mx-auto" />
                        }
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {usage.webSearchUsed && <span className="inline-flex px-1.5 py-0.5 text-xs bg-purple-50 text-purple-700 rounded">Search</span>}
                          {usage.reasoningUsed && <span className="inline-flex px-1.5 py-0.5 text-xs bg-orange-50 text-orange-700 rounded">Reasoning</span>}
                          {usage.hasImage && <span className="inline-flex px-1.5 py-0.5 text-xs bg-pink-50 text-pink-700 rounded">Image</span>}
                          {usage.hasDocument && <span className="inline-flex px-1.5 py-0.5 text-xs bg-cyan-50 text-cyan-700 rounded">Doc</span>}
                          {usage.functionCallingUsed && <span className="inline-flex px-1.5 py-0.5 text-xs bg-green-50 text-green-700 rounded">Functions</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!tableLoading && totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-400">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filteredUsage.length)} of {filteredUsage.length.toLocaleString()}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  // show pages around current page
                  let p = i + 1;
                  if (totalPages > 7) {
                    if (safePage <= 4) p = i + 1;
                    else if (safePage >= totalPages - 3) p = totalPages - 6 + i;
                    else p = safePage - 3 + i;
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-7 h-7 text-xs rounded-md transition-colors ${
                        p === safePage ? 'bg-indigo-600 text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
